import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, MapPin, TrendingUp, DollarSign, Megaphone } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const GoogleAdsHoustonPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/google-ads-houston');

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Google Ads en Houston",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": { "@type": "City", "name": "Houston", "containedIn": { "@type": "State", "name": "Texas" } },
    "description": "Gestión profesional de campañas Google Ads en Houston, TX. Segmentación local precisa y medición orientada a negocio."
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Google Ads en Houston TX | Gestión de campañas PPC – Luis ROC</title>
        <meta 
          name="description" 
          content="Campañas Google Ads en Houston orientadas a convertir demanda en oportunidades medibles. Segmentación local + audiencia bilingüe. Reunión inicial gratuita de 30 minutos." 
        />
        <meta name="keywords" content="google ads houston, ppc houston, publicidad google houston, agencia google ads houston, campañas ppc houston" />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="geo.placename" content="Houston" />
      </Helmet>

      <JsonLd data={serviceSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav 
            items={[
              { name: "Servicios", url: "/servicios" },
              { name: "Localidades", url: "/servicios-locales-houston" },
              { name: "Google Ads Houston", url: "/google-ads-houston" }
            ]}
            language="es"
          />

          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              Google Ads en Houston orientado a convertir demanda en oportunidades medibles
            </h1>
            
            <div className="max-w-4xl mx-auto mb-8">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Houston es un mercado altamente competitivo para PPC. Trabajamos la segmentación local, 
                las audiencias bilingües (EN/ES) y la optimización basada en señales como CPA, conversiones y calidad de la demanda. 
                Desde Midtown hasta Energy Corridor, desde Galleria hasta Medical Center — la segmentación busca concentrar 
                la inversión en audiencias y ubicaciones relevantes.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button className="whitespace-normal h-auto py-3 text-center" 
                size="lg" 
                onClick={() => window.location.href = '/contacto'}
              >
                Agenda tu reunión inicial gratuita de 30 minutos
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => window.location.href = '/diagnostico-seo-gratis'}
              >
                Ver Estrategia Completa
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-primary" />
                Gestión profesional de Google Ads
              </span>
              <span className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-primary" />
                Medición de CPA y ROAS
              </span>
            </div>
          </section>

          <section className="py-16 bg-accent/5 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">
              Por qué Google Ads funciona en Houston
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Activación de demanda</h3>
                    <p className="text-muted-foreground">
                      Google Ads puede activar demanda más rápidamente que SEO, pero el rendimiento depende del mercado, inversión, oferta, competencia y medición.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Segmentación por código postal en Houston</h3>
                    <p className="text-muted-foreground">
                      Enfoca tus anuncios solo a ZIP codes rentables. No desperdicies presupuesto en áreas fuera de tu zona de servicio.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Audiencia bilingüe optimizada</h3>
                    <p className="text-muted-foreground">
                      Campañas paralelas EN/ES con mensajes adaptados a cada audiencia: distinto vocabulario de búsqueda, distinta landing y medición separada por idioma.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </section>

          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-6">
              Revisa tu estrategia de Google Ads en Houston
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              reunión inicial gratuita de 30 minutos para revisar el contexto y definir siguientes pasos
            </p>
            <Button className="whitespace-normal h-auto py-3 text-center" 
              size="lg"
              variant="secondary"
              onClick={() => window.location.href = '/contacto'}
            >
              Agendar reunión inicial gratuita de 30 minutos
            </Button>
          </section>

          <section className="py-8">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-4">Otros servicios en Houston</h3>
              <div className="flex flex-wrap justify-center gap-4">
                <Button variant="outline" onClick={() => window.location.href = '/seo-houston'}>
                  SEO Houston
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/google-ads-the-woodlands'}>
                  Google Ads The Woodlands
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/servicios-locales-houston'}>
                  Ver Todas las Localidades
                </Button>
              </div>
            </div>
          </section>
        </main>

        <KnowledgeConnections path="/google-ads-houston" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default GoogleAdsHoustonPage;
